import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import {
  handleDeleteNotificationService,
  handleDeleteSelectedNotificationsService,
  handleGetNotificationsService,
  handleMarkAllNotificationsAsReadService,
} from "../services/notification";
import QUERY_KEYS from "../constants/queryKeys";
import { useSnackbar } from "../context/SnackbarContextProvider";

type GetNotificationsParams = {
  page?: number;
  limit?: number;
  type?: string;
};

export const useNotifications = ({
  page = 1,
  limit = 10,
  type,
}: GetNotificationsParams = {}) => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();

  /* =======================
     Get Notifications
     ======================= */
  const notificationsQuery = useQuery({
    queryKey: [QUERY_KEYS.GET_NOTIFICATIONS, page, limit, type],
    queryFn: () =>
      handleGetNotificationsService({
        page,
        limit,
        type,
      }),
    // refetchInterval: 2 * 60 * 1000, // 2 minutes
    placeholderData: keepPreviousData,
  });

  /* =======================
     Mark all as read
     ======================= */
  const markAllAsReadMutation = useMutation({
    mutationKey: [QUERY_KEYS.MARK_ALL_NOTIFICATIONS_AS_READ],
    mutationFn: handleMarkAllNotificationsAsReadService,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_NOTIFICATIONS],
      });
    },
  });

  /* =======================
     Delete notification
     ======================= */
  const deleteNotificationMutation = useMutation({
    mutationKey: [QUERY_KEYS.DELETE_NOTIFICATION],
    mutationFn: (id: string) => handleDeleteNotificationService(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_NOTIFICATIONS],
      });

      showSnackbar({
        message: "ההתראה נמחקה בהצלחה",
        type: "success",
      });
    },
  });

  /* =======================
     Delete selected notifications
     ======================= */
  const deleteSelectedNotificationsMutation = useMutation({
    mutationKey: [QUERY_KEYS.DELETE_SELECTED_NOTIFICATIONS],
    mutationFn: handleDeleteSelectedNotificationsService,
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.GET_NOTIFICATIONS],
      });

      showSnackbar({
        message: "ההתראות נמחקו בהצלחה",
        type: "success",
      });
    },
  });

  return {
    /* data */
    notificationsData: notificationsQuery.data,
    notifications: notificationsQuery.data?.data?.notifications ?? [],
    unreadCount: notificationsQuery.data?.data?.unreadCount ?? 0,

    /* states */
    isLoading: notificationsQuery.isLoading,
    isFetching: notificationsQuery.isFetching,
    isError: notificationsQuery.isError,
    isDeletingNotification: deleteNotificationMutation.isPending,
    isDeletingSelectedNotifications:
      deleteSelectedNotificationsMutation.isPending,

    /* actions */
    refetchNotifications: notificationsQuery.refetch,
    markAllAsRead: markAllAsReadMutation.mutate,
    deleteNotification: deleteNotificationMutation.mutate,
    deleteSelectedNotifications: deleteSelectedNotificationsMutation.mutate,
  };
};
